// frontend/src/main.tsx

// Sentry setup
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import ErrorBoundary from './components/shared/ErrorBoundary';
import GlobalErrorHandler from './components/shared/GlobalErrorHandler';
import { getAppConfig } from './config/apps';
import { AppProvider } from './context/AppContext';
import { AuthProvider } from './context/AuthContext';
import { PermissionProvider } from './context/PermissionContext';
import { env } from './lib/env';
import { router } from './routes';

const appConfig = getAppConfig(env.VITE_APP_ID);

// Only initialize Sentry if the DSN is provided and analytics are not disabled
if (env.VITE_SENTRY_DSN && !env.VITE_DISABLE_ANALYTICS) {
  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    environment: env.VITE_APP_ENV,
    release: __COMMIT_SHA__,

    // Tunnel through backend to bypass ad blockers
    // Uses API base URL since frontend and backend may be on different domains
    tunnel: `${env.VITE_API_BASE_URL}/api/v1/sentry-tunnel`,

    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
    ],

    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,

    // Suppress expected errors: 401 auth expiry, WebAuthn user cancellation
    ignoreErrors: [
      'Request failed with status code 401',
      'Authentication ceremony was sent an abort signal',
    ],

    tracePropagationTargets: ['localhost', /^\//, new RegExp(env.VITE_API_BASE_URL)],
  });
}

import './index.css';
import 'nprogress/nprogress.css';

// Enhanced QueryClient with better error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: true,
      retry: (failureCount, error: any) => {
        // Don't retry on authentication errors
        if (error?.response?.status === 401) return false;
        // Don't retry on client errors (4xx) except 408, 429
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return error?.response?.status === 408 || error?.response?.status === 429;
        }
        // Retry server errors up to 3 times
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: (failureCount, error: any) => {
        // Don't retry mutations on client errors
        if (error?.response?.status >= 400 && error?.response?.status < 500) return false;
        // Retry server errors once
        return failureCount < 1;
      },
    },
  },
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error(
    "Failed to find the root element. Make sure there is a div with id='root' in your index.html."
  );
}

function AppWithErrorHandling() {
  return (
    <ErrorBoundary
      onError={(error: any, errorInfo: any) => {
        console.error('App-level error:', error, errorInfo);
      }}
    >
      <GlobalErrorHandler />
      <AppProvider config={appConfig}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <PermissionProvider>
              <RouterProvider router={router} />
            </PermissionProvider>
          </AuthProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </AppProvider>
    </ErrorBoundary>
  );
}

// Render the app
createRoot(rootElement).render(
  <StrictMode>
    <AppWithErrorHandling />
  </StrictMode>
);
